import { useEffect, useState } from 'react';
import type { Overlay } from '@lumacast/contracts';

type Clock = Extract<Overlay, { type: 'clock' }>;
type Ticker = Extract<Overlay, { type: 'ticker' }>;
type Corner = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
type Size = 'small' | 'medium' | 'large';

const SIZES: Record<Size, { w: number; h: number }> = { small: { w: 1300, h: 800 }, medium: { w: 1800, h: 1100 }, large: { w: 2400, h: 1500 } };
const SPEEDS = { slow: 80, normal: 140, fast: 220 } as const;
const HEIGHTS = { thin: 600, standard: 800, large: 1200 } as const;
const MARGIN_X = 300;
const MARGIN_Y = 400;
const newId = () => `o-${crypto.randomUUID().slice(0, 8)}`;

/** Corner placement that stays clear of a ticker on the same edge. */
function place(corner: Corner, size: Size, ticker: Ticker | undefined): Pick<Clock, 'x' | 'y' | 'w' | 'h'> {
  const { w, h } = SIZES[size];
  const top = corner.startsWith('top');
  const left = corner.endsWith('left');
  const tickerHere = ticker && ticker.position === (top ? 'top' : 'bottom') ? ticker.heightBp : 0;
  return { w, h, x: left ? MARGIN_X : 10_000 - MARGIN_X - w, y: top ? MARGIN_Y + tickerHere : 10_000 - MARGIN_Y - h - tickerHere };
}
function cornerOf(c: Clock): Corner {
  return `${c.y + c.h / 2 < 5000 ? 'top' : 'bottom'}-${c.x + c.w / 2 < 5000 ? 'left' : 'right'}` as Corner;
}
function sizeOf(c: Clock): Size {
  return (Object.entries(SIZES).find(([, s]) => s.w === c.w)?.[0] as Size | undefined) ?? 'medium';
}
const nearest = <T extends Record<string, number>>(table: T, v: number) =>
  (Object.entries(table).sort((a, b) => Math.abs(a[1] - v) - Math.abs(b[1] - v))[0]![0]) as keyof T;

/** Live clock/ticker overlays for a playlist: shown by players on top of every item of the loop. */
export function OverlaysPanel({ value, onChange, editable }: { value: Overlay[]; onChange: (o: Overlay[]) => void; editable: boolean }) {
  const clock = value.find((o): o is Clock => o.type === 'clock');
  const ticker = value.find((o): o is Ticker => o.type === 'ticker');
  const set = (next: { clock?: Clock | null; ticker?: Ticker | null }) => {
    const c = next.clock === undefined ? clock : next.clock ?? undefined;
    const t = next.ticker === undefined ? ticker : next.ticker ?? undefined;
    // Keep the clock clear of the ticker when either moves.
    const placedClock = c ? { ...c, ...place(cornerOf(c), sizeOf(c), t) } : undefined;
    onChange([placedClock, t].filter((x): x is Clock | Ticker => !!x));
  };
  const [lines, setLines] = useState((ticker?.items ?? []).join('\n'));
  useEffect(() => setLines((ticker?.items ?? []).join('\n')), [ticker?.overlayId]); // eslint-disable-line react-hooks/exhaustive-deps
  const tooLong = lines.split('\n').some((l) => l.trim().length > 280);

  return (
    <section aria-labelledby="ov-head" style={{ borderTop: '1px solid var(--border)', padding: 16 }}>
      <h3 id="ov-head" style={{ margin: '0 0 4px' }}>Live overlays</h3>
      <p className="subtle" style={{ margin: '0 0 12px' }}>Drawn live by the player on top of every item in this playlist. Players update the clock every second in the screen's local time.</p>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, alignItems: 'start' }}>
        <fieldset className="card card-pad" disabled={!editable} style={{ margin: 0 }}>
          <legend className="row" style={{ gap: 8 }}>
            <input id="ov-clock" type="checkbox" checked={!!clock} onChange={(e) => set({ clock: e.target.checked ? { type: 'clock', overlayId: newId(), format: '12h', showSeconds: false, showDate: true, color: '#FFFFFF', background: '#103868', ...place('top-right', 'medium', ticker) } : null })} />
            <label htmlFor="ov-clock"><strong>Clock</strong></label>
          </legend>
          {clock && (
            <div className="grid" style={{ gap: 10 }}>
              <div className="field"><label htmlFor="ov-corner">Corner</label>
                <select id="ov-corner" className="input" value={cornerOf(clock)} onChange={(e) => set({ clock: { ...clock, ...place(e.target.value as Corner, sizeOf(clock), ticker) } })}>
                  <option value="top-right">Top right</option><option value="top-left">Top left</option><option value="bottom-right">Bottom right</option><option value="bottom-left">Bottom left</option>
                </select></div>
              <div className="field"><label htmlFor="ov-size">Size</label>
                <select id="ov-size" className="input" value={sizeOf(clock)} onChange={(e) => set({ clock: { ...clock, ...place(cornerOf(clock), e.target.value as Size, ticker) } })}>
                  <option value="small">Small</option><option value="medium">Medium</option><option value="large">Large</option>
                </select></div>
              <div className="field"><label htmlFor="ov-format">Format</label>
                <select id="ov-format" className="input" value={clock.format} onChange={(e) => set({ clock: { ...clock, format: e.target.value as Clock['format'] } })}>
                  <option value="12h">12-hour (3:05 PM)</option><option value="24h">24-hour (15:05)</option>
                </select></div>
              <label className="row"><input type="checkbox" checked={clock.showSeconds} onChange={(e) => set({ clock: { ...clock, showSeconds: e.target.checked } })} /> Show seconds</label>
              <label className="row"><input type="checkbox" checked={clock.showDate} onChange={(e) => set({ clock: { ...clock, showDate: e.target.checked } })} /> Show date</label>
              <div className="row">
                <label className="row">Text <input type="color" value={clock.color} onChange={(e) => set({ clock: { ...clock, color: e.target.value.toUpperCase() } })} aria-label="Clock text color" /></label>
                <label className="row">Background <input type="color" value={clock.background ?? '#000000'} disabled={clock.background === null} onChange={(e) => set({ clock: { ...clock, background: e.target.value.toUpperCase() } })} aria-label="Clock background color" /></label>
                <label className="row"><input type="checkbox" checked={clock.background === null} onChange={(e) => set({ clock: { ...clock, background: e.target.checked ? null : '#103868' } })} /> No background</label>
              </div>
            </div>
          )}
        </fieldset>
        <fieldset className="card card-pad" disabled={!editable} style={{ margin: 0 }}>
          <legend className="row" style={{ gap: 8 }}>
            <input id="ov-ticker" type="checkbox" checked={!!ticker} onChange={(e) => set({ ticker: e.target.checked ? { type: 'ticker', overlayId: newId(), position: 'bottom', heightBp: HEIGHTS.standard, items: ['Your message here'], separator: '•', speedPxPerSec: SPEEDS.normal, color: '#FFFFFF', background: '#C01C38' } : null })} />
            <label htmlFor="ov-ticker"><strong>Ticker</strong></label>
          </legend>
          {ticker && (
            <div className="grid" style={{ gap: 10 }}>
              <div className="field">
                <label htmlFor="ov-lines">Messages (one per line)</label>
                <textarea id="ov-lines" className="input" rows={4} value={lines} aria-invalid={tooLong} aria-describedby="ov-lines-hint"
                  onChange={(e) => {
                    setLines(e.target.value);
                    // Plain text only; control characters are stripped, empty lines ignored.
                    const items = e.target.value.split('\n').map((l) => l.replace(/[\u0000-\u001f\u007f]/g, '').trim()).filter(Boolean);
                    if (items.length && items.length <= 50 && items.every((l) => l.length <= 280)) set({ ticker: { ...ticker, items } });
                  }} />
                <span id="ov-lines-hint" className={tooLong ? 'error' : 'subtle'}>{tooLong ? 'Each message can be at most 280 characters.' : 'Up to 50 messages, shown as plain text.'}</span>
              </div>
              <div className="field"><label htmlFor="ov-pos">Position</label>
                <select id="ov-pos" className="input" value={ticker.position} onChange={(e) => set({ ticker: { ...ticker, position: e.target.value as Ticker['position'] } })}>
                  <option value="bottom">Bottom</option><option value="top">Top</option>
                </select></div>
              <div className="field"><label htmlFor="ov-speed">Speed</label>
                <select id="ov-speed" className="input" value={nearest(SPEEDS, ticker.speedPxPerSec)} onChange={(e) => set({ ticker: { ...ticker, speedPxPerSec: SPEEDS[e.target.value as keyof typeof SPEEDS] } })}>
                  <option value="slow">Slow</option><option value="normal">Normal</option><option value="fast">Fast</option>
                </select></div>
              <div className="field"><label htmlFor="ov-height">Bar height</label>
                <select id="ov-height" className="input" value={nearest(HEIGHTS, ticker.heightBp)} onChange={(e) => set({ ticker: { ...ticker, heightBp: HEIGHTS[e.target.value as keyof typeof HEIGHTS] } })}>
                  <option value="thin">Thin</option><option value="standard">Standard</option><option value="large">Large</option>
                </select></div>
              <div className="row">
                <label className="row">Text <input type="color" value={ticker.color} onChange={(e) => set({ ticker: { ...ticker, color: e.target.value.toUpperCase() } })} aria-label="Ticker text color" /></label>
                <label className="row">Background <input type="color" value={ticker.background} onChange={(e) => set({ ticker: { ...ticker, background: e.target.value.toUpperCase() } })} aria-label="Ticker background color" /></label>
              </div>
            </div>
          )}
        </fieldset>
        <OverlayPreview clock={clock} ticker={ticker} />
      </div>
    </section>
  );
}

/** Static placement preview (not a screenshot of a player). */
function OverlayPreview({ clock, ticker }: { clock?: Clock; ticker?: Ticker }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const time = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: clock?.showSeconds ? '2-digit' : undefined, hour12: clock?.format !== '24h' });
  return (
    <figure style={{ margin: 0 }}>
      <div role="img" aria-label={`Preview: ${clock ? `clock ${cornerOf(clock).replace('-', ' ')}` : 'no clock'}, ${ticker ? `ticker at the ${ticker.position}` : 'no ticker'}`}
        style={{ position: 'relative', aspectRatio: '16 / 9', background: 'linear-gradient(135deg, #1b2430, #3a4a5e)', borderRadius: 12, overflow: 'hidden', containerType: 'size' }}>
        {clock && (
          <div style={{ position: 'absolute', left: `${clock.x / 100}%`, top: `${clock.y / 100}%`, width: `${clock.w / 100}%`, height: `${clock.h / 100}%`, background: clock.background ?? 'transparent', color: clock.color, borderRadius: 6, display: 'grid', placeContent: 'center', textAlign: 'center', fontWeight: 650, lineHeight: 1.1, fontSize: `${(clock.h / 100) * (clock.showDate ? 0.4 : 0.55)}cqh` }}>
            <span style={{ whiteSpace: 'nowrap' }}>{time}</span>
            {clock.showDate && <span style={{ fontSize: '0.45em', fontWeight: 500 }}>{now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>}
          </div>
        )}
        {ticker && (
          <div style={{ position: 'absolute', left: 0, right: 0, [ticker.position]: 0, height: `${ticker.heightBp / 100}%`, background: ticker.background, color: ticker.color, display: 'flex', alignItems: 'center', overflow: 'hidden', whiteSpace: 'nowrap', fontWeight: 600, fontSize: `${(ticker.heightBp / 100) * 0.55}cqh`, paddingLeft: '2%' }}>
            {ticker.items.join(`   ${ticker.separator}   `)}
          </div>
        )}
      </div>
      <figcaption className="subtle" style={{ marginTop: 6 }}>Placement preview, not a player screenshot. The ticker scrolls on screens.</figcaption>
    </figure>
  );
}
